import React, {useState, useEffect, useCallback} from 'react';
import {Popconfirm} from 'antd';
import '../App.css';
import {ToastContainer, toast} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:8011';

const LedgerList = () => {
  const [ledger, setLedger] = useState([]);
  const [filteredLedger, setFilteredLedger] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterDate, setFilterDate] = useState('');
  const [persons, setPersons] = useState([]);
  const [filterPerson, setFilterPerson] = useState('');
  const [userNames, setUserNames] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentLedger, setCurrentLedger] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  const fetchUserNames = useCallback(async (userIds) => {
    try {
      const userNames = {};
      for (const id of userIds) {
        const response = await fetch(`${API_BASE_URL}/auth/users/${id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch user name for ID ${id}`);
        }

        const data = await response.json();
        userNames[id] = data.name;
      }
      setUserNames(userNames);
    } catch (error) {
      console.error('Error fetching user names:', error.message);
      toast.error(`Failed to load user names: ${error.message}`);
    }
  }, []);

  const fetchLedger = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No token found');

      const response = await fetch(`${API_BASE_URL}/ledger/getCompleteLedger`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch Ledger');
      const ledgerData = await response.json();
      const sortedLedger = ledgerData.sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );

      setLedger(sortedLedger);
      setFilteredLedger(sortedLedger);

      // Fetch user names
      const userIds = [...new Set(ledgerData.map((led) => led.userId))];
      fetchUserNames(userIds);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [fetchUserNames]);

  const fetchPersons = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/persons/getAllPersons`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch persons');
      const data = await response.json();
      if (Array.isArray(data)) {
        setPersons(data);
      } else {
        throw new Error('Persons data is not an array');
      }
    } catch (error) {
      console.error('Error fetching persons:', error);
      toast.error('Failed to load persons');
    }
  }, []);

  useEffect(() => {
    fetchLedger();
    fetchPersons();
  }, [fetchLedger, fetchPersons]);

  const handleFilterDateChange = (event) => {
    const selectedDate = event.target.value;
    setFilterDate(selectedDate);
    filterLedger(selectedDate, filterPerson);
  };

  const handleFilterPersonChange = (event) => {
    const selectedPerson = event.target.value;
    setFilterPerson(selectedPerson);
    filterLedger(filterDate, selectedPerson);
  };

  const filterLedger = (date, person) => {
    let filtered = ledger;

    if (date) {
      filtered = filtered.filter((ledger) => {
        const ledgerDate = new Date(ledger.date).toISOString().split('T')[0];
        return ledgerDate === date;
      });
    }

    if (person) {
      filtered = filtered.filter((ledger) => ledger.person === person);
    }

    setFilteredLedger(filtered);
  };

  const handleRefresh = () => {
    fetchLedger();
    setFilterDate('');
    setFilterPerson('');
  };

  // Calculate running balances for filtered ledger
  const calculateRunningBalances = (ledgerEntries) => {
    const runningBalances = {}; // Initialize the object to keep track of balances
    const entriesWithBalance = [];

    ledgerEntries.forEach((entry) => {
      if (!runningBalances[entry.person]) {
        runningBalances[entry.person] = 0;
      }

      if (entry.type === 'Credit') {
        runningBalances[entry.person] -= entry.price;
      } else if (entry.type === 'Debit') {
        runningBalances[entry.person] += entry.price;
      }

      // Push the entry with the updated balance
      entriesWithBalance.push({
        ...entry,
        balance: runningBalances[entry.person],
      });
    });

    return entriesWithBalance;
  };

  const balances = calculateRunningBalances(filteredLedger);

  // Delete a ledger by ID
  const deleteLedgerById = async (id) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No token found');

      const response = await fetch(
        `${API_BASE_URL}/ledger/deleteLedgerItem/${id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error('Failed to delete ledger');
      toast.success('Ledger deleted successfully');
      // Refresh the ledger list after deletion
      fetchLedger();
    } catch (error) {
      console.error('Error deleting ledger:', error);
      toast.error('Failed to delete ledger');
    }
  };

  // Handle modal opening
  const openModal = (ledger) => {
    setCurrentLedger(ledger);
    setIsModalOpen(true);
  };

  // Handle modal closing
  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentLedger(null);
  };

  // Handle file upload for image
  const handleImageChange = (e) => {
    setImageFile(e.target.files[0]);
  };

  const uploadImage = async (image) => {
    const data = new FormData();
    data.append('file', image);
    data.append('upload_preset', 'DecentBuilders');
    try {
      const response = await fetch(
        'https://api.cloudinary.com/v1_1/dwbwxh3sj/image/upload',
        {
          method: 'POST',
          body: data,
        }
      );
      const urlData = await response.json();
      return urlData.url;
    } catch (error) {
      console.error(error);
      toast.error('Image upload failed.');
      return null;
    }
  };
  // Update a ledger (including image if uploaded)
  const updateLedger = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No token found');

      let imageUrl = currentLedger.imagePath; // Use existing image if not changed

      // If new image is uploaded, upload it to Cloudinary first
      if (imageFile) {
        const uploadedImageUrl = await uploadImage(imageFile);
        if (uploadedImageUrl) {
          imageUrl = uploadedImageUrl;
        } else {
          throw new Error('Failed to upload image');
        }
      }

      const updatedLedgerData = {
        title: currentLedger.title,
        description: currentLedger.description,
        price: currentLedger.price,
        person: currentLedger.person,
        project: currentLedger.project,
        type: currentLedger.type,
        date: currentLedger.date,
        imagePath: imageUrl, // Updated image URL
      };

      const response = await fetch(
        `${API_BASE_URL}/ledger/updateLedger/${currentLedger._id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updatedLedgerData),
        }
      );

      if (!response.ok) throw new Error('Failed to update ledger');

      toast.success('Ledger updated successfully');
      closeModal();
      fetchLedger(); // Refresh ledger after update
    } catch (error) {
      console.error('Error updating ledger:', error);
      toast.error('Failed to update ledger');
    }
  };

  return (
    <div className="bg-white h-full p-2">
      <div className="mb-4 flex flex-col md:flex-row md:justify-between md:items-center">
        <div className="mb-4 md:mb-0">
          <label
            htmlFor="filterDate"
            className="block text-sm font-bold text-themeColor2 mb-2"
          >
            Filter by Date:
          </label>
          <input
            type="date"
            id="filterDate"
            value={filterDate}
            onChange={handleFilterDateChange}
            className="border border-gray-300 rounded-md p-2 w-full md:w-auto"
          />
        </div>

        <button
          onClick={handleRefresh}
          className="bg-themeColor1 text-themeColor2 px-4 py-2 rounded-md mb-4 md:mb-0"
        >
          <i className="fa-solid fa-arrows-rotate"></i>
        </button>

        <div>
          <label
            htmlFor="filterPerson"
            className="block text-sm font-bold text-themeColor2 mb-2"
          >
            Filter by Person:
          </label>
          <select
            id="filterPerson"
            value={filterPerson}
            onChange={handleFilterPersonChange}
            className="border border-gray-300 rounded-md p-2 w-full md:w-auto"
          >
            <option value="">Select Person</option>
            {persons.map((per) => (
              <option key={per._id} value={per.person}>
                {per.person}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        {loading ? (
          <div className="flex justify-center items-center h-48 mt-20">
            <div className="loader">
              <div className="newtons-cradle">
                <div className="newtons-cradle__dot"></div>
                <div className="newtons-cradle__dot"></div>
                <div className="newtons-cradle__dot"></div>
                <div className="newtons-cradle__dot"></div>
              </div>
            </div>
          </div>
        ) : error ? (
          <p>Error: {error}</p>
        ) : (
          <div className="relative">
            <div className="overflow-y-auto" style={{maxHeight: '500px'}}>
              <table className="min-w-full divide-y divide-gray-200 border">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Person
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Project
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Balance
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Added By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {balances.length > 0 ? (
                    balances.map((ledger) => (
                      <tr key={ledger._id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {ledger.person}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {ledger.project}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {ledger.title}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          Rs. {ledger.price.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {ledger.type}
                        </td>
                        <td
                          className={`px-6 py-4 whitespace-nowrap text-sm ${
                            ledger.balance < 0
                              ? 'balance-negative'
                              : 'balance-positive'
                          }`}
                        >
                          {ledger.balance.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(ledger.date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {userNames[ledger.userId] || 'Unknown'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {ledger.description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 flex gap-2">
                          <i
                            className="fa-regular fa-pen-to-square cursor-pointer"
                            onClick={() => openModal(ledger)}
                          ></i>
                          <Popconfirm
                            title="Are you sure you want to delete?"
                            onConfirm={() => deleteLedgerById(ledger._id)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <i className="fa-solid fa-trash-can"></i>
                          </Popconfirm>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="10"
                        className="text-center px-6 py-4 text-gray-500"
                      >
                        <i className="fa-solid fa-box-open text-gray-400 text-4xl mb-2"></i>
                        <p>No data available</p>
                      </td>
                    </tr>
                  )}
                </tbody>
                {/* Footer removed */}
              </table>
            </div>
          </div>
        )}
      </div>
      {/* Modal for Editing Ledger */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-4 rounded-lg shadow-lg w-80">
            <div className="">
              <h2 className="text-xl font-semibold mb-4">Edit Ledger</h2>
              <div className="mb-4">
                <label className="block text-sm font-bold mb-2">Title</label>
                <input
                  type="text"
                  value={currentLedger.title}
                  onChange={(e) =>
                    setCurrentLedger({...currentLedger, title: e.target.value})
                  }
                  className="border border-gray-300 rounded-md p-2 w-full"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-bold mb-2">Amount</label>
                <input
                  type="text"
                  value={currentLedger.price}
                  onChange={(e) =>
                    setCurrentLedger({...currentLedger, price: e.target.value})
                  }
                  className="border border-gray-300 rounded-md p-2 w-full"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-bold mb-2">
                  Description
                </label>
                <textarea
                  value={currentLedger.description}
                  onChange={(e) =>
                    setCurrentLedger({
                      ...currentLedger,
                      description: e.target.value,
                    })
                  }
                  className="border border-gray-300 rounded-md p-2 w-full"
                />
              </div>
              {/* Other fields like price, person, type, etc. */}
              <div className="mb-4">
                <label className="block text-sm font-bold mb-2">Image</label>
                <input
                  type="file"
                  onChange={handleImageChange}
                  className="border border-gray-300 rounded-md p-2"
                />
                {currentLedger.imagePath && (
                  <img
                    src={currentLedger.imagePath}
                    alt="Ledger"
                    className="mt-2 w-32 h-32"
                  />
                )}
              </div>
              <div className="flex justify-end">
                <button
                  className="bg-themeColor1 text-white px-4 py-2 rounded-md"
                  onClick={updateLedger}
                >
                  Save
                </button>
                <button
                  className="ml-2 bg-gray-300 text-gray-800 px-4 py-2 rounded-md"
                  onClick={closeModal}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default LedgerList;
